/* eslint-disable max-len */
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Form,
  Grid,
  Header,
  Label,
  Segment,
} from 'semantic-ui-react';
import { required } from '@mhub/web-validations';

import {
  HIMS_LOT_POSITION,
  HIMS_LOT_POSITION_OPTIONS,
  HIMS_STRATA_UNIT_FIELDS,
  HIMS_UNIT_FIELDS,
} from '../constants/hims';
import { BANKS } from '../constants/banks';
import { VA_BANK_NAME, VA_UNIT_FIELDS } from '../constants/virtualAccount';
import SelectBank from '../components/AppForm/SelectBank';
import AppForm from './AppForm';
import { checkRegex } from '../components/RegexDropdownPicker';

const RenderDivider = () => <Divider className="compact" />;

export default class CustomFieldForm extends Component {
  static propTypes = {
    componentData: PropTypes.oneOfType([
      PropTypes.object,
    ]).isRequired,
    customFieldSettings: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    countryCode: PropTypes.string,
    isEditing: PropTypes.bool,
    isBookingCustomField: PropTypes.bool,
    onCustomInputChange: PropTypes.func.isRequired,
    onCustomSelectChange: PropTypes.func.isRequired,
    hasProjectHimsEnabled: PropTypes.bool,
    vaIsEnabled: PropTypes.bool,
    hasHimsStrataEnabled: PropTypes.bool,
    minItems: PropTypes.number,
    offsetFrom: PropTypes.number,
    mobile: PropTypes.number,
    tablet: PropTypes.number,
    computer: PropTypes.number,
  }
  static defaultProps = {
    mobile: 16,
    tablet: 16,
    computer: 8,
  }
  constructor(props) {
    super(props);
    this.validation = {};
    // add validateRef for all custom fields
    if (props.componentData) {
      if (props.componentData.bookingCustomFields && props.componentData.bookingCustomFields.length > 0) {
        props.componentData.bookingCustomFields.forEach((key) => {
          this.validation[this.getPrefix(key.projectBookingCustomField.name)] = createRef();
        });
      }
      if (props.componentData.projectUnitCustomFields && props.componentData.projectUnitCustomFields.length > 0) {
        props.componentData.projectUnitCustomFields.forEach((key) => {
          this.validation[this.getPrefix(key.name)] = createRef();
        });
      }
    }
    if (props.customFieldSettings && props.customFieldSettings.length > 0) {
      props.customFieldSettings.filter(cf => !cf.isDefault && !cf.isHidden).forEach((key) => {
        this.validation[this.getPrefix(key.name)] = createRef();
      });
    }
  }
  componentDidUpdate(prevProps) {
    const { isBookingCustomField, componentData } = this.props;
    if (isBookingCustomField && this.props.isEditing && !prevProps.isEditing) {
      if (isBookingCustomField && componentData.bookingCustomFields && componentData.bookingCustomFields.length > 0) {
        componentData.bookingCustomFields
          .filter(bcf => !bcf.projectBookingCustomField.isDefault && !bcf.projectBookingCustomField.isHidden)
          .sort((a, b) => a.projectBookingCustomField.position - b.projectBookingCustomField.position)
          .forEach((d) => {
            if (this.validation[this.getPrefix(d.projectBookingCustomField.name)] && this.validation[this.getPrefix(d.projectBookingCustomField.name)].current) {
              return this.validation[this.getPrefix(d.projectBookingCustomField.name)].current.validate(componentData[this.getPrefix(d.projectBookingCustomField.name)]);
            }
            return null;
          });
      }
    }
  }
  getPrefix = (v) => {
    const { isBookingCustomField } = this.props;
    if (!isBookingCustomField) {
      return `ucf_${v}`;
    }
    return `bcf_${v}`;
  }

  isRequiredAtStatus = (field) => {
    const { componentData, isBookingCustomField } = this.props;
    
    // Only applies to booking custom fields
    if (!isBookingCustomField || !field.isRequired) {
      return field.isRequired;
    }

    // For DEFAULT fields (purchaser fields), always use isRequired directly
    // Status-based logic only applies to custom booking fields
    if (field.isDefault) {
      return field.isRequired;
    }

    const currentStatus = componentData.statusID || 'reserved';
    const requiredAtStatus = field.requiredAtStatus || 'reserved';

    // Define status hierarchy: reserved < booked < sold_spa_signed
    const statusHierarchy = {
      'reserved': 1,
      'booked': 2,
      'sold_spa_signed': 3,
    };

    const currentLevel = statusHierarchy[currentStatus];
    const requiredLevel = statusHierarchy[requiredAtStatus];

    if (!currentLevel || !requiredLevel) {
      return false;
    }

    return currentLevel >= requiredLevel;
  }

  renderLabel = label => ({
    content: label.text,
    circular: true,
  });
  renderInputField = (data, idx) => {
    const { componentData, isEditing, onCustomInputChange } = this.props;
    const isHimsUnitField = HIMS_UNIT_FIELDS.includes(data.name);
    const nonEditHimsField = isHimsUnitField && !HIMS_STRATA_UNIT_FIELDS.includes(data.name);
    if (isEditing) {
      return (
        <Form.Group widths="equal">
          <AppForm.Input
            key={idx}
            disabled={data.isDisabled}
            autoComplete="off"
            label={`${data.label}${this.isRequiredAtStatus(data) ? '*' : ''} ${nonEditHimsField ? '(read only)' : ''}`}
            name={this.getPrefix(data.name)}
            onChange={e => onCustomInputChange(e, this.validation)}
            placeholder={data.placeholder}
            ref={this.validation[this.getPrefix(data.name)]}
            validation={(this.isRequiredAtStatus(data) && !data.isDisabled) ?
            [required, {
              validate: value => checkRegex(value, data),
            }] : [{
              validate: value => checkRegex(value, data),
            }]}
            value={componentData[this.getPrefix(data.name)]}
            validateLabel={data.label}
            readOnly={nonEditHimsField}
          />
        </Form.Group>
      );
    }
    return (
      <div key={idx} >
        {(isHimsUnitField && idx < 2) ? '' : <RenderDivider /> }
        <Grid stretched>
          <Grid.Row>
            <Grid.Column textAlign="left" width={6} className="word_break_word">
              <b>{data.label}</b>
            </Grid.Column>
            <Grid.Column textAlign="right" width={10} className="word_break_word">
              {componentData[this.getPrefix(data.name)] || '-'}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
  renderTextareaField = (data, idx) => {
    const { componentData, isEditing, onCustomInputChange } = this.props;
    if (isEditing) {
      return (
        <Form.Group widths="equal">
          <AppForm.Validate
            key={idx}
            validation={(this.isRequiredAtStatus(data) && !data.isDisabled) ? [required] : null}
            ref={this.validation[this.getPrefix(data.name)]}
            onChange={onCustomInputChange}
            validateLabel={data.label}
          >
            <Form.TextArea
              disabled={data.isDisabled}
              autoComplete="off"
              label={`${data.label}${this.isRequiredAtStatus(data) ? '*' : ''}`}
              name={this.getPrefix(data.name)}
              placeholder={data.placeholder}
              ref={this.validation[this.getPrefix(data.name)]}
              value={componentData[this.getPrefix(data.name)]}
              style={{ minHeight: '6em', height: '6em' }}
            />
          </AppForm.Validate>
        </Form.Group>
      );
    }
    return (
      <div key={idx}>
        <RenderDivider />
        <Grid stretched>
          <Grid.Row>
            <Grid.Column textAlign="left" width={6} className="word_break_word">
              <b>{data.label}</b>
            </Grid.Column>
            <Grid.Column textAlign="right" width={10} className="word_break_word">
              {componentData[this.getPrefix(data.name)] || '-'}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
  renderDropdownField = (data, idx, oriData) => {
    const { componentData, countryCode, isEditing, onCustomSelectChange, isBookingCustomField } = this.props;
    let customFieldOptions = [];
    let optionText = '';
    let value = componentData[this.getPrefix(data.name)];

    if (!isBookingCustomField && oriData && oriData.projectUnitCustomFieldSettingOptions) {
      oriData.projectUnitCustomFieldSettingOptions.forEach((d) => {
        customFieldOptions.push(
          { text: d.title, value: d.value, id: d.id },
        );
      });
    }

    if (!isBookingCustomField && data.name === HIMS_LOT_POSITION) {
      customFieldOptions = HIMS_LOT_POSITION_OPTIONS;
    }
    if (!isBookingCustomField && data.name === VA_BANK_NAME) {
      customFieldOptions = BANKS;
    }

    if (isBookingCustomField && oriData && oriData.projectBookingCustomFieldOptions) {
      // keep the Project > Booking Fields order; the API array is not guaranteed sorted
      [...oriData.projectBookingCustomFieldOptions].sort((a, b) => a.position - b.position).forEach((d) => {
        customFieldOptions.push(
          { text: d.title, value: d.value, id: d.id },
        );
      });
    }

    if (data.type === 'dropdown_multi') {
      if (componentData[this.getPrefix(data.name)]) {
        const selectedOptions = customFieldOptions.filter((o) => {
          let match = false;
          componentData[this.getPrefix(data.name)].split(',').forEach((v) => {
            if (v === o.value) match = true;
          });
          return match;
        });
        optionText = selectedOptions.map((v, key) => (<Label key={key} circular size="small">{v.text}</Label>));
      }
      if (componentData[this.getPrefix(data.name)] && value !== '') {
        value = value.split(',');
      } else {
        value = [];
      }
    } else if (data.type === 'dropdown') {
      const option = customFieldOptions.find(d => d.value === componentData[this.getPrefix(data.name)]);
      if (option) {
        optionText = option.text;
      }
    }

    // return
    if (isEditing) {
      if (!isBookingCustomField && data.name === VA_BANK_NAME) {
        return (
          <SelectBank
            country={countryCode}
            validation={(this.isRequiredAtStatus(data) && !data.isDisabled) ? [required] : null}
            validateRef={this.validation[this.getPrefix(data.name)]}
            validateLabel={data.label}
            label={`${data.label}${this.isRequiredAtStatus(data) ? '*' : ''}`}
            onChange={(e, d) => onCustomSelectChange(e, d, customFieldOptions, this.validation)}
            name={this.getPrefix(data.name)}
            placeholder={data.placeholder}
            value={value}
          />
        );
      }
      return (
        <Form.Group widths="equal">
          <AppForm.Validate
            key={idx}
            validation={(this.isRequiredAtStatus(data) && !data.isDisabled) ? [required] : null}
            ref={this.validation[this.getPrefix(data.name)]}
            onChange={(e, d) => onCustomSelectChange(e, d, customFieldOptions, this.validation)}
            validateLabel={data.label}
          >
            <Form.Select
              disabled={data.isDisabled}
              label={`${data.label}${this.isRequiredAtStatus(data) ? '*' : ''}`}
              name={this.getPrefix(data.name)}
              options={customFieldOptions}
              placeholder={data.placeholder}
              search
              selectOnBlur={false}
              value={value}
              multiple={data.type === 'dropdown_multi'}
              renderLabel={this.renderLabel}
              clearable
            />
          </AppForm.Validate>
        </Form.Group>
      );
    }
    return (
      <div key={idx}>
        <RenderDivider />
        <Grid stretched>
          <Grid.Row>
            <Grid.Column textAlign="left" width={6} className="word_break_word">
              <b>{data.label}</b>
            </Grid.Column>
            <Grid.Column textAlign="right" width={10} className="word_break_word">
              <div>{optionText || '-'}</div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }
  renderCustomField = (field, idx, isCustomFieldSetting = false, dataCustomFields = []) => {
    const { isBookingCustomField, hasProjectHimsEnabled, hasHimsStrataEnabled, vaIsEnabled } = this.props;
    let data = field;
    if (!isCustomFieldSetting) {
      data = isBookingCustomField ? field.projectBookingCustomField : field.projectUnitCustomFieldSetting;
    } else if (isCustomFieldSetting && dataCustomFields && dataCustomFields.length > 0) {
      const existingField = isBookingCustomField ? dataCustomFields.find(d => d.projectBookingCustomField.id === data.id) : dataCustomFields.find(d => d.projectUnitCustomFieldSetting.id === data.id);
      if (existingField) return '';
    }

    if (!isBookingCustomField && data.isDefault) {
      // set HIMS unit fields based on project settings
      if (!hasProjectHimsEnabled && HIMS_UNIT_FIELDS.includes(data.name)) return '';
      if (!hasHimsStrataEnabled && HIMS_STRATA_UNIT_FIELDS.includes(data.name)) return '';

      // set Virtual Account fields based on global settings
      if (!vaIsEnabled && VA_UNIT_FIELDS.includes(data.name)) return '';
    }

    if (data.type === 'input') return this.renderInputField(data, idx);
    if (data.type === 'textarea') return this.renderTextareaField(data, idx);
    if (data.type === 'dropdown' || data.type === 'dropdown_multi') return this.renderDropdownField(data, idx, field);

    return '';
  }
  renderAllCustomFields = (isHims = false) => {
    const { componentData, customFieldSettings, isBookingCustomField, minItems, offsetFrom } = this.props;
    const isUnitCustomField = !isBookingCustomField;
    const renderCFs = [];
    if (isBookingCustomField && componentData.bookingCustomFields && componentData.bookingCustomFields.length > 0) {
      componentData.bookingCustomFields
        .filter(bcf => !bcf.projectBookingCustomField.isDefault && !bcf.projectBookingCustomField.isHidden)
        .sort((a, b) => a.projectBookingCustomField.position - b.projectBookingCustomField.position)
        .forEach((d, idx) => renderCFs.push(this.renderCustomField(d, idx, false)));
    }
    if (isUnitCustomField && componentData.projectUnitCustomFields && componentData.projectUnitCustomFields.length > 0) {
      componentData.projectUnitCustomFields
        .filter(ucf => !ucf.projectUnitCustomFieldSetting.isHidden)
        // show only HIMS unit fields if isHims is true, otherwise exclude them
        .filter(ucf => (isHims ? HIMS_UNIT_FIELDS.includes(ucf.projectUnitCustomFieldSetting.name) : !HIMS_UNIT_FIELDS.includes(ucf.projectUnitCustomFieldSetting.name)))
        .sort((a, b) => a.projectUnitCustomFieldSetting.position - b.projectUnitCustomFieldSetting.position)
        .forEach((d, idx) => renderCFs.push(this.renderCustomField(d, idx, false)));
    }
    if (isBookingCustomField && customFieldSettings && customFieldSettings.length > 0) {
      customFieldSettings
        .filter(cf => !cf.isDefault && !cf.isHidden)
        .sort((a, b) => a.position - b.position)
        .map((d, idx) => renderCFs.push(this.renderCustomField(d, idx, true, isBookingCustomField ? componentData.bookingCustomFields : componentData.projectUnitCustomFields)));
    }
    if (isUnitCustomField && customFieldSettings && customFieldSettings.length > 0) {
      customFieldSettings
        .filter(cf => !cf.isHidden)
        // show only HIMS unit fields if isHims is true, otherwise exclude them
        .filter(cf => (isHims ? HIMS_UNIT_FIELDS.includes(cf.name) : !HIMS_UNIT_FIELDS.includes(cf.name)))
        .sort((a, b) => a.position - b.position)
        .map((d, idx) => renderCFs.push(this.renderCustomField(d, idx, true, isBookingCustomField ? componentData.bookingCustomFields : componentData.projectUnitCustomFields)));
    }
    if (minItems && minItems > 0) {
      return renderCFs.slice(0, minItems);
    }
    if (offsetFrom && offsetFrom > 0) {
      renderCFs.splice(0, offsetFrom);
    }
    return renderCFs;
  };
  renderHimsUnitFieldsBox = () => {
    const { mobile, tablet, computer } = this.props;
    return (
      <Grid.Column width={16} style={{ marginTop: '10px' }}>
        <Header className="hims_unit_fields" as="h5" attached="top" textAlign="center">
          HIMS INFORMATION
        </Header>
        <Segment attached >
          <Grid style={{ marginTop: '10px', marginBottom: '4px' }}>
            <Grid.Row className="no-top-padding">
              {this.renderAllCustomFields(true).map((element, idx) => (
                element ? (
                  <Grid.Column key={idx} mobile={mobile} tablet={tablet} computer={computer}>
                    {element}
                  </Grid.Column>
                ) : ''
              ))}
            </Grid.Row>
          </Grid>
        </Segment>
      </Grid.Column>
    );
  }
  render() {
    const { mobile, tablet, computer, hasProjectHimsEnabled, isEditing } = this.props;
    return (
      <Grid>
        <Grid.Row className="no-top-padding">
          {this.renderAllCustomFields().map((element, idx) => (
            element ? (
              <Grid.Column key={idx} mobile={mobile} tablet={tablet} computer={computer}>
                {element}
              </Grid.Column>
            ) : ''
          ))}
        </Grid.Row>

        {/* HIMS unit fields */}
        { isEditing && hasProjectHimsEnabled &&
          <Grid.Column width={16}>
            <Divider />
            <Header textAlign="center" size="small" style={{ paddingBottom: '10px' }}>HIMS Information</Header>
          </Grid.Column>
        }
        { isEditing && hasProjectHimsEnabled &&
          <Grid.Row className="no-top-padding">
            {this.renderAllCustomFields(true).map((element, idx) => (
              element ? (
                <Grid.Column key={idx} mobile={mobile} tablet={tablet} computer={computer}>
                  {element}
                </Grid.Column>
              ) : ''
            ))}
          </Grid.Row>
        }
        {!isEditing && hasProjectHimsEnabled && this.renderHimsUnitFieldsBox()}
      </Grid>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/CustomFieldForm.jsx